import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Modal,
  Text,
  Group,
  Button,
  Stack,
  Chip,
  SimpleGrid,
  ActionIcon,
} from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { useAuth } from '../../context/AuthContext';
import { usersService } from '../../services/users';
import { INTEREST_CATEGORIES } from '../../utils/categories';

const PAGE_SIZE = 15;
const TOTAL_PAGES = Math.ceil(INTEREST_CATEGORIES.length / PAGE_SIZE);

interface OnboardingModalProps {
  opened: boolean;
  onClose: () => void;
}

export function OnboardingModal({ opened, onClose }: OnboardingModalProps) {
  const { updateUser } = useAuth();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string[]>([]);
  const [page, setPage] = useState(0);

  const pageItems = INTEREST_CATEGORIES.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const saveMutation = useMutation({
    mutationFn: (interests: string[]) =>
      usersService.updateProfile({
        categoryInterests: interests,
        hasCompletedOnboarding: true,
      }),
    onSuccess: (updatedUser) => {
      updateUser(updatedUser);
      queryClient.invalidateQueries({ queryKey: ['feed-discover'] });
      onClose();
    },
  });

  const toggle = (label: string) => {
    setSelected((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
    );
  };

  const handleSkip = () => saveMutation.mutate([]);
  const handleSave = () => saveMutation.mutate(selected);

  return (
    <Modal
      opened={opened}
      onClose={handleSkip}
      title="Welcome to CartCritic!"
      size="xl"
      centered
      closeOnClickOutside={false}
      closeOnEscape={false}
      withCloseButton={false}
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          What kinds of product reviews are you interested in? We'll use this to personalise your discover feed.
        </Text>

        <SimpleGrid cols={3} spacing="xs">
          {pageItems.map(({ label, emoji }) => (
            <Chip
              key={label}
              checked={selected.includes(label)}
              onChange={() => toggle(label)}
              radius="xl"
              size="sm"
              styles={{ label: { width: '100%', justifyContent: 'center' } }}
            >
              {emoji} {label}
            </Chip>
          ))}
        </SimpleGrid>

        {/* Pagination row */}
        <Group justify="center" gap="xs">
          <ActionIcon
            variant="subtle"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            <IconChevronLeft size={16} />
          </ActionIcon>

          {Array.from({ length: TOTAL_PAGES }).map((_, i) => (
            <ActionIcon
              key={i}
              size="xs"
              radius="xl"
              variant={i === page ? 'filled' : 'subtle'}
              onClick={() => setPage(i)}
              aria-label={`Page ${i + 1}`}
            >
              {i + 1}
            </ActionIcon>
          ))}

          <ActionIcon
            variant="subtle"
            disabled={page === TOTAL_PAGES - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            <IconChevronRight size={16} />
          </ActionIcon>
        </Group>

        {selected.length > 0 && (
          <Text size="xs" c="dimmed" ta="center">
            {selected.length} selected across all pages
          </Text>
        )}

        <Group justify="space-between">
          <Button
            variant="subtle"
            color="gray"
            size="sm"
            onClick={handleSkip}
            loading={saveMutation.isPending && selected.length === 0}
          >
            Skip for now
          </Button>
          <Button
            onClick={handleSave}
            loading={saveMutation.isPending && selected.length > 0}
            disabled={selected.length === 0}
          >
            {selected.length === 0
              ? 'Select at least one'
              : `Continue with ${selected.length} selected`}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
